import React, { useState, useEffect, useMemo } from 'react';
import { Grid, Input, Slider, Stack, Typography } from '@mui/material';
import { updateQuoteBrokeragePercentage } from '../../helper/formatQuoteForUpdate';
import CreateQuoteSectionTitle from '../CreateQuoteSectionTitle';
import { colours } from '../../shared/colors';
import { useQuoteContext } from '../../shared/contexts/QuoteContext';
import { QuoteGroupResponse } from '../../shared/types/quotes';
import { useUpdateQuoteGroupMutation } from '../../services/features/quoteApi/quoteApi';

/**
 * Component for Commission Slider.
 *
 * @param {Object} props - Component props.
 *
 * @returns {JSX.Element} The JSX element for the Commission Slider.
 */
export const CommissionSlider = (props: any) => {
	const [brokeragePercentage, setBrokeragePercentage] = useState<number>(20);

	const {
		quoteGroup,
		currentQuotes,
		loadingProducts,
		setLoadingProducts,
		isReferred,
		isDeclined,
		isQuoteInProgress,
		isReferralApprovedModifiable,
	} = useQuoteContext();

	const [updateQuoteGroup] = useUpdateQuoteGroupMutation();

	const readonly = !isQuoteInProgress && !isReferralApprovedModifiable;

	useEffect(() => {
		if (currentQuotes.length > 0) {
			setBrokeragePercentage(currentQuotes[0].brokeragePercentage);
		}
	}, [currentQuotes]);

	const handleSliderChange = (event: Event, value: number | number[], activeThumb: number) => {
		if (typeof value === 'number') {
			updateQuotePercentage(Math.max(0, Math.min(20, value)));
		}
	};

	const updateQuotePercentage = async (newValue: number) => {
		setBrokeragePercentage(newValue);
	};

	useEffect(() => {
		const getData = setTimeout(async () => {
			if (quoteGroup) {
				if (quoteGroup.quotes.some((q) => q.brokeragePercentage != brokeragePercentage)) {
					setLoadingProducts(true);

					const update = updateQuoteBrokeragePercentage(quoteGroup, brokeragePercentage);
					await updateQuoteGroup({ quote: update, currentQuote: quoteGroup })
						.unwrap()
						.catch((error) =>
							console.log('Error in updating brokerage percentage', error)
						);
					setLoadingProducts(false);
				}
			}
		}, 1000);
		return () => clearTimeout(getData);
	}, [brokeragePercentage, quoteGroup]);

	const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const newValue =
			event.target.value === '' ? brokeragePercentage : Number(event.target.value);
		if (newValue != brokeragePercentage) {
			updateQuotePercentage(Math.max(0, Math.min(20, newValue)));
		}
	};

	const handleBlur = () => {
		const newValue = Number(brokeragePercentage);
		updateQuotePercentage(Math.max(0, Math.min(20, newValue)));
	};

	return (
		<Stack>
			<CreateQuoteSectionTitle title={'Brokerage Commission Percentage'} />
			<Grid container spacing={3}>
				<Grid item xs={6} sx={{ ...(readonly && { cursor: 'not-allowed' }) }}>
					<Slider
						disabled={readonly || loadingProducts}
						value={typeof brokeragePercentage === 'number' ? brokeragePercentage : 0}
						onChange={handleSliderChange}
						min={0}
						max={20}
						sx={{
							color: 'white',
							height: '9px',
							borderRadius: '20px',
							'& .MuiSlider-rail': {
								border: '1px solid grey',
							},
							'& .MuiSlider-thumb': {
								backgroundColor: '#fff',
								border: `1px solid ${colours.primary.main}`,
								width: '25px',
								height: '25px',
							},
							'& .MuiSlider-track': {
								color: readonly
									? colours.primaryDark.hover
									: colours.primaryDark.main,
							},
						}}
					/>
				</Grid>
				<Grid item xs={2}>
					<Input
						disabled={readonly || loadingProducts}
						value={brokeragePercentage}
						size='medium'
						onChange={handleInputChange}
						onBlur={handleBlur}
						inputProps={{
							min: 0,
							max: 20,
							type: 'number',
							...(readonly && { sx: { cursor: 'not-allowed' } }),
						}}
					/>
				</Grid>
			</Grid>
		</Stack>
	);
};
